import { FieldType } from '@databases/model/field';
import {
  type MutablePageLinkValue,
  type PageLinkValue,
  type SinglePageLinkValue,
  type ValueJSON
} from '@databases/model/value';
import * as Y from 'yjs';

import { SynchronizedArray } from '../helpers/synchronized-array';
import { SynchronizedMapElement } from '../helpers/synchronized-map-element';
import { SynchronizedRecord } from '../helpers/synchronized-record';
import { type DisposableElement, type WrappedYType } from '../helpers/types';
import { ValueProperties } from '../y-entry';
import { FieldMapping } from '../y-field';

const PageLinkValueProperties = {
  contentId: 'c',
  ari: 'a',
  id: 'i'
};

class YSinglePageLinkValue extends SynchronizedRecord {
  static mapper(value: unknown): YSinglePageLinkValue | undefined {
    if (value instanceof Y.Map) {
      return new YSinglePageLinkValue(value);
    }
  }

  get contentId(): string | undefined {
    return this.getString(PageLinkValueProperties.contentId, undefined);
  }

  set contentId(val: string | undefined) {
    this.set(PageLinkValueProperties.contentId, val);
  }

  get ari(): string | undefined {
    return this.getString(PageLinkValueProperties.ari, undefined);
  }

  set ari(val: string | undefined) {
    this.set(PageLinkValueProperties.ari, val);
  }

  get id(): string {
    return this.getString(PageLinkValueProperties.id, '');
  }

  toJSON(): SinglePageLinkValue {
    return {
      contentId: this.contentId,
      ari: this.ari,
      id: this.id
    };
  }

  static fromJson(value: SinglePageLinkValue): YSinglePageLinkValue {
    return new YSinglePageLinkValue(
      new Y.Map([
        [PageLinkValueProperties.contentId, value.contentId],
        [PageLinkValueProperties.ari, value.ari],
        [PageLinkValueProperties.id, value.id]
      ])
    );
  }
}

export class YPageLinkValue
  implements PageLinkValue, MutablePageLinkValue, WrappedYType, DisposableElement
{
  private valueElement: SynchronizedMapElement<SynchronizedArray<YSinglePageLinkValue>>;

  constructor(private input: Y.Map<any>) {
    this.valueElement = this.createValueElement();
  }

  private createValueElement() {
    return new SynchronizedMapElement<SynchronizedArray<YSinglePageLinkValue>>(
      this.input,
      ValueProperties.value,
      (yarray) => {
        if (yarray instanceof Y.Array) {
          return new SynchronizedArray(
            yarray as Y.Array<unknown>,
            YSinglePageLinkValue.mapper
          );
        }
      },
      () => new Y.Array()
    );
  }

  get type(): FieldType.PageLink {
    return FieldType.PageLink;
  }

  get isEmpty() {
    return !this.valueElement.value?.toArray().length;
  }

  get value() {
    return this.valueElement.value?.toArray() ?? [];
  }

  add(value: SinglePageLinkValue): void {
    this.valueElement.valueRequired.push([YSinglePageLinkValue.fromJson(value)]);
  }

  delete(index: number): void {
    this.valueElement.valueRequired.delete(index);
  }

  reorder(index: number, newIndex: number): void {
    const val = this.valueElement.valueRequired.get(index).toJSON();
    this.valueElement.valueRequired.delete(index);
    this.valueElement.valueRequired.insert(newIndex, [
      YSinglePageLinkValue.fromJson(val)
    ]);
  }

  setTo(values: SinglePageLinkValue[]) {
    const valueRef = new Y.Array();
    this.input.set(ValueProperties.value, valueRef);
    this.valueElement = this.createValueElement();

    this.valueElement.valueRequired.push(
      values.map((v) => YSinglePageLinkValue.fromJson(v))
    );
  }

  toJSON(): ValueJSON<FieldType.PageLink, SinglePageLinkValue[]> {
    return {
      type: this.type,
      value: this.value.map((v) => v.toJSON())
    };
  }

  static fromJSON(json: ValueJSON<FieldType.PageLink, SinglePageLinkValue[]>) {
    return new YPageLinkValue(
      new Y.Map([
        [ValueProperties.type, FieldMapping[json.type]],
        [
          ValueProperties.value,
          Y.Array.from(json.value.map((v) => YSinglePageLinkValue.fromJson(v).unwrap()))
        ]
      ])
    );
  }

  unwrap(): Y.AbstractType<any> {
    return this.input;
  }

  dispose(): void {
    this.valueElement.dispose();
  }
}
